import { useFeatureFlag } from "@/contexts/FeatureFlagContext"
import {
  useGetUserConfig,
  useUpdateUserConfig,
} from "@/services/api/userConfigService"
import { useGetUserData } from "@/services/api/userService"
import type { UserConfigPayload } from "@/types/userConfig"
import { useEffect, useRef, useState } from "react"

export const welcomeSuppressionKey = (userId: string, organizationId: string) =>
  `turnus:data-room-welcome:${encodeURIComponent(userId)}:${encodeURIComponent(organizationId)}`

const isSuppressed = (key: string) => {
  try {
    return localStorage.getItem(key) !== null
  } catch {
    return false
  }
}

interface UseDataRoomWelcomeOptions {
  treeSucceeded: boolean
  hasFolderParam: boolean
  busy: boolean
}

export const useDataRoomWelcome = ({
  treeSucceeded,
  hasFolderParam,
  busy,
}: UseDataRoomWelcomeOptions) => {
  const onboarding = useFeatureFlag("ONBOARDING_DATA_ROOM")
  const { data: user } = useGetUserData(onboarding)
  const configQuery = useGetUserConfig(onboarding)
  const { mutateAsync } = useUpdateUserConfig()
  const [session, setSession] = useState<{
    identity: string
    sound: boolean
  } | null>(null)
  // The DOM gate below (open dialog, focused input) is not React state, so
  // the effect re-runs on focus changes and clicks to re-check it.
  const [domTick, setDomTick] = useState(0)
  const dismissed = useRef(new Set<string>())
  const closing = useRef(false)
  const identity =
    user?.id && user.organization
      ? welcomeSuppressionKey(user.id, user.organization)
      : null
  const available = Boolean(
    onboarding && identity && treeSucceeded && !hasFolderParam && !busy,
  )
  const config = configQuery.data?.configuration
  // Mirrors useTaggingFlowVideoOnboarding: read the query's own reactive
  // data, not a raw cache lookup. This ref just carries the latest render's
  // value into the async persist below, it never reaches past the query
  // observer that keeps it alive.
  const latestConfig = useRef(config)
  latestConfig.current = config
  const eligible =
    available && configQuery.isSuccess && config && typeof config === "object"

  useEffect(() => {
    if (session) {
      if (!available || session.identity !== identity) setSession(null)
      return
    }
    if (
      eligible &&
      identity &&
      !closing.current &&
      !document.querySelector(
        '[role="dialog"], [role="alertdialog"], [role="menu"]',
      ) &&
      !document.activeElement?.matches(
        'input, textarea, [contenteditable="true"]',
      ) &&
      config.onboarding?.dataRoomWelcome !== true &&
      !dismissed.current.has(identity) &&
      !isSuppressed(identity)
    ) {
      setSession({
        identity,
        sound: config.onboarding?.dataRoomWelcomeSound !== false,
      })
    }
  }, [available, eligible, identity, config, session, domTick])

  useEffect(() => {
    if (session || !eligible) return
    const recheck = () => setDomTick((tick) => tick + 1)
    document.addEventListener("focusin", recheck)
    document.addEventListener("focusout", recheck)
    document.addEventListener("pointerup", recheck)
    return () => {
      document.removeEventListener("focusin", recheck)
      document.removeEventListener("focusout", recheck)
      document.removeEventListener("pointerup", recheck)
    }
  }, [session, eligible])

  const dismiss = () => {
    if (!session || closing.current) return
    closing.current = true
    setSession(null)
    if (!available) return
    // Dismissal is local and immediate; once the write succeeds it does not
    // auto-show again. This is not an exactly-once guarantee across devices.
    dismissed.current.add(session.identity)
    try {
      localStorage.setItem(session.identity, "true")
    } catch {
      // In-memory suppression still covers this mounted page.
    }
    const persist = async () => {
      for (let attempt = 0; attempt < 2; attempt++) {
        const latest = latestConfig.current
        if (!latest) return
        try {
          await mutateAsync({
            configuration: {
              ...latest,
              onboarding: {
                ...latest.onboarding,
                dataRoomWelcome: true,
                dataRoomWelcomeSound: session.sound,
              },
            } as UserConfigPayload["configuration"],
          })
          return
        } catch {
          // One best-effort retry, merging the newest known config again.
        }
      }
    }
    void persist()
  }

  return {
    open: Boolean(session && available && session.identity === identity),
    sound: session?.sound ?? true,
    dismiss,
    toggleSound: () =>
      setSession((current) =>
        current ? { ...current, sound: !current.sound } : null,
      ),
    afterExit: () => {
      closing.current = false
    },
  }
}
